import * as React from 'react';
import classnames from 'classnames';

import AddNew from 'components/buttons/add-new/AddNew';
import MetricsChannelsBasicChannelsHOC from 'components/metrics/channels-basic/MetricsChannelsBasicChannelsHOC';
import EntityName from 'components/entity/entity-name/EntityName';
import EntityAvatar from 'components/entity/entity-avatar/EntityAvatar';

import CampaignPayoutsVariablePaymentsModalFields from '../fields/CampaignPayoutsVariablePaymentsModalFields';

import { __t, applyTranslations } from 'utils/language';
import { sumVariablePayments } from 'utils/payouts';
import { renderPrice } from 'utils/helper/price';
import { getEntityFromProfile } from 'utils/view';

import './campaign-payouts-variable-payments-modal-body.scss';

import type { SubcampaignInfluencerProfile, TextEntry, VariablePayment } from 'types';

import type { SyntheticEvent } from 'react';
import VerticalSpacing from 'components/vertical-spacing/VerticalSpacing';

applyTranslations(require('./campaign-payouts-variable-payments-modal-body.i18n.json'));

type Props = {
  className: string,
  agreedPayment: number,
  variablePayments: Array<VariablePayment>,
  influencerProfile: SubcampaignInfluencerProfile,
  otherArrangement: boolean,
  isLoading: boolean,
  subcampaignId: number,
  onFieldAdd: (a: number) => void,
  onFieldRemove: (b: number, a: number) => void,
  onFieldChange: (event: SyntheticEvent<TextEntry>, a: number) => void
};

const msgKey = 'campaign_payouts_variable_payments_modal_body';
const NUM_PAYMENTS_BEFORE_SCROLL = 7;

class CampaignPayoutsVariablePaymentsModalBody extends React.PureComponent<Props> {
  static defaultProps = {
    className: ''
  };

  handleFieldAdd = (): void => {
    const { subcampaignId, onFieldAdd } = this.props;
    onFieldAdd(subcampaignId);
  };

  handleFieldRemove = (id: number): void => {
    const { subcampaignId, onFieldRemove } = this.props;
    onFieldRemove(id, subcampaignId);
  };

  handleFieldChange = (event: SyntheticEvent<TextEntry>): void => {
    const { subcampaignId, onFieldChange } = this.props;
    onFieldChange(event, subcampaignId);
  };

  handleFieldFocus = (event: SyntheticEvent<TextEntry>): void => {
    const { subcampaignId, onFieldChange } = this.props;
    onFieldChange(event, subcampaignId);
  };

  renderAddNew(): React.ReactNode {
    return (
      <div className="campaign-payouts-variable-payments-modal-body__add-new">
        <AddNew
          text={__t(`${msgKey}.labels.add_payment`)}
          icon={{
            name: 'plus-inside-circle-custom-bg-white-fg',
            width: 16,
            height: 16
          }}
          onClick={this.handleFieldAdd}
        />
      </div>
    );
  }

  renderTotal(): React.ReactNode {
    const { variablePayments } = this.props;
    const variablePaymentSum = sumVariablePayments(variablePayments);

    return (
      <div className="campaign-payouts-variable-payments-modal-body__total">
        <div className="campaign-payouts-variable-payments-modal-body__total-label">
          {__t(`${msgKey}.labels.total_payment`)}:
        </div>
        <div className="campaign-payouts-variable-payments-modal-body__total-value">
          {renderPrice(variablePaymentSum, { separateThousands: false })}
        </div>
      </div>
    );
  }

  renderInfluencerInfo(): React.ReactNode {
    const { influencerProfile, agreedPayment, otherArrangement } = this.props;
    const entity = getEntityFromProfile(influencerProfile);

    const outputValue = otherArrangement
      ? __t(`${msgKey}.labels.other_arrangement`)
      : renderPrice(agreedPayment);

    return (
      <div className="campaign-payouts-variable-payments-modal-body__influencer">
        <div className="campaign-payouts-variable-payments-modal-body__avatar">
          <EntityAvatar
            entity={entity}
            customSize={{ width: 44, height: 44 }}
            form="round"
            link={entity?.url}
            openInNewTab
            bordered
          />
        </div>
        <div className="campaign-payouts-variable-payments-modal-body__details">
          <EntityName
            entity={entity}
            link={entity?.url}
            className="campaign-payouts-variable-payments-modal-body__entity-name"
            openInNewTab
          />
          <VerticalSpacing type="tiny" />
          <MetricsChannelsBasicChannelsHOC
            channels={influencerProfile.channels}
            showChannels={['instagram', 'tiktok', 'youtube']}
            layout="inline"
            type="limited"
            includeRatio
          />
          <div className="campaign-payouts-variable-payments-modal-body__agreed-payment">
            {`${__t(`${msgKey}.labels.agreed_offer`)}: ${outputValue}`}
          </div>
        </div>
      </div>
    );
  }

  renderContent(): React.ReactNode {
    const { variablePayments, influencerProfile, isLoading } = this.props;

    return (
      <div className="campaign-payouts-variable-payments-modal-body__content">
        {this.renderInfluencerInfo()}
        <div className="campaign-payouts-variable-payments-modal-body__fields">
          <CampaignPayoutsVariablePaymentsModalFields
            variablePayments={variablePayments}
            influencerProfile={influencerProfile}
            isLoading={isLoading}
            onFieldRemove={this.handleFieldRemove}
            onFieldChange={this.handleFieldChange}
            onFieldFocus={this.handleFieldFocus}
          />
          {this.renderAddNew()}
          {this.renderTotal()}
        </div>
      </div>
    );
  }

  render(): React.ReactNode {
    const { className, variablePayments } = this.props;
    const classes = classnames('campaign-payouts-variable-payments-modal-body', {
      [`${String(className)}`]: Boolean(className)
    });

    return (
      <div className={classes}>
        <div className="campaign-payouts-variable-payments-modal-body__content-container">
          {this.renderContent()}
        </div>
        {
          variablePayments.length >= NUM_PAYMENTS_BEFORE_SCROLL && (
            <div className="campaign-payouts-variable-payments-modal-body__scroll-shadow" />
          )
        }
      </div>
    );
  }
}

export default CampaignPayoutsVariablePaymentsModalBody;
